/**
 * 학생 홈의 "오늘 복습" 할 일(오답 중심 간격 복습). 일정·판정은 서버(GET /me/review-tasks)가 계산하고 이 화면은 표시만 한다.
 * - 오늘 복습(예정일 도래) / 기한 지남 / 예정을 구분한다. 완료·다음 일정은 응답 직후 서버 값으로 자동 반영한다.
 * - 회상 화면에는 정답·해설·정답 강조 문장이 없다. 보기만 보이고, 응답 뒤에도 정답 번호를 알려 주지 않는다(해설은 결과 화면의 '정답·해설 보기').
 * - 조회 실패는 빈 목록과 다른 문구로 보이고 지난 목록은 숨긴다. 간격은 제안 기본값(원장 미확정)임을 화면에 밝힌다.
 * - 같은 선택의 재전송은 같은 요청 키, 선택이 바뀌면 새 키. 요청 중에는 단추를 잠가 중복 제출을 막는다.
 */
import { h, clear } from './dom.js';
import { currentGen, newRequestKey, type Transport } from '../core/transport.js';
import type { ItemsResponse } from '../core/types.js';
import { toggleButton } from './toggle-button.js';

export interface ReviewTask {
  itemId: string; attemptId: string; pointId: string; displayNumber: string; releaseTitle: string;
  state: 'due' | 'overdue' | 'upcoming' | 'done'; dueAt: string | null; stage: number; totalStages: number; rounds: number; lastStudyAt: string; ruleVersion: string;
  /** 'wrong-answer-recall' = GR 지점 다시 고르기, 'evidence-recall' = 근거 객관식 재확인(서로 다른 제출 계약) */
  kind?: 'wrong-answer-recall' | 'evidence-recall';
  evidenceQuestion?: { stem: string; options: { id: string; no: number; text: string }[] } | null;
  evidenceUnavailable?: boolean;
  history?: { round: number; result: 'correct' | 'incorrect'; respondedAt: string; infoProvidedBefore: boolean }[];
}
export type ReviewKindFilter = 'all' | 'gr' | 'evidence';
const isEv = (x: ReviewTask): boolean => x.kind === 'evidence-recall';
const solvable = (x: ReviewTask): boolean => (x.state === 'due' || x.state === 'overdue') && !(isEv(x) && x.evidenceUnavailable === true);

/**
 * 제출 뒤 다음에 열 복습 항목을 고른다. 현재 탭(상태)과 종류 필터가 정한 "세션 범위" 안에서만 고르고,
 * 화면에 그려진 DOM 여부로 판단하지 않는다(페이지 밖 항목도 범위에 있으면 대상이다).
 * - next: 범위 안의 다음 풀 항목(방금 푼 항목 제외). index는 범위 목록 안의 위치(페이지 계산용).
 * - otherSolvable: 범위 밖(다른 탭·종류)에 아직 풀 수 있는 항목 수. 범위 완료와 전체 완료를 구분한다.
 */
export function pickNext(tasks: readonly ReviewTask[], scope: { tab: ReviewTask['state'] | null; kind: ReviewKindFilter; tabs: boolean }, justDone: string): { next: ReviewTask | null; index: number; scopeLeft: number; otherSolvable: number } {
  const inKind = (x: ReviewTask) => !scope.tabs || scope.kind === 'all' || (scope.kind === 'evidence') === isEv(x);
  const inScope = (x: ReviewTask) => solvable(x) && inKind(x) && (!scope.tabs || scope.tab === null || x.state === scope.tab);
  const list = tasks.filter((x) => inScope(x));
  const rest = list.filter((x) => x.itemId !== justDone);
  const next = rest[0] ?? null;
  return { next, index: next === null ? -1 : list.indexOf(next), scopeLeft: rest.length, otherSolvable: tasks.filter((x) => solvable(x) && x.itemId !== justDone && !inScope(x)).length };
}

const KST = { timeZone: 'Asia/Seoul', month: 'long', day: 'numeric', weekday: 'short' } as const;
export const dayText = (iso: string | null): string => (iso === null ? '완료' : new Intl.DateTimeFormat('ko-KR', KST).format(new Date(iso)));

export const TASK_GROUPS: readonly { state: ReviewTask['state']; title: string; hint: string }[] = [
  { state: 'due', title: '오늘 복습', hint: '복습할 때가 된 오답이에요.' },
  { state: 'overdue', title: '기한 지난 복습', hint: '예정일이 지났어요. 지금 풀어도 돼요.' },
  { state: 'upcoming', title: '예정된 복습', hint: '아직 풀 때가 아니에요.' },
];

const PAGE = 10;
export interface ReviewMountOpts { readonly limit?: number; readonly onMore?: () => void; readonly tabs?: boolean }

export function mountReviewTasks(root: HTMLElement, t: Transport, opts: ReviewMountOpts = {}): void {
  let tab: ReviewTask['state'] | null = null;
  let kindFilter: ReviewKindFilter = 'all';
  let page = 1;
  const gen = currentGen();
  const live = () => gen === currentGen() && root.isConnected;
  const keys = new Map<string, { sig: string; key: string }>();
  let open: string | null = null;
  let tasks: ReviewTask[] = [];
  const session = { total: 0, done: 0 }; // 이번 접속에서 이어 푸는 "오늘 복습" 진행(서버 일정과 별개의 표시용)
  const rows = new Map<string, { li: HTMLElement; x: ReviewTask }>();

  /** 목록을 서버에서 다시 받아 그린다. 받아 왔으면 true, 실패(또는 화면 이탈)면 false — 실패를 완료로 읽지 않기 위해 호출부가 확인한다 */
  async function load(note = ''): Promise<boolean> {
    clear(root);
    root.append(h('p', { class: 'loading' }, '복습 할 일을 불러오고 있어요'));
    const r = await t.request('GET', '/me/review-tasks');
    if (!live()) return false;
    clear(root);
    if (r.kind !== 'http' || r.status !== 200) {
      // 조회 실패는 "할 일 없음"이 아니다. 지난 표시는 숨긴다
      tasks = [];
      root.append(...(note !== '' ? [h('p', { role: 'status', 'data-testid': 'rt-note' }, note)] : []),
        h('p', { class: 'notice', role: 'alert', 'data-testid': 'rt-error' }, '복습 할 일을 불러오지 못했어요. 할 일이 없는 것이 아니라 받아 오지 못한 상태예요.'),
        h('button', { class: 'secondary', 'data-testid': 'rt-retry', onclick: () => void load() }, '다시 불러오기'));
      return false;
    }
    tasks = r.body['tasks'] as ReviewTask[];
    rows.clear();
    if (note !== '') root.append(h('p', { role: 'status', 'data-testid': 'rt-note' }, note));
    if (tasks.length === 0) return true; // 오답이 없으면 아무것도 만들지 않는다
    const box = h('section', { class: 'rtasks', 'data-testid': 'review-tasks', 'aria-label': '오답 복습' }, h('h3', {}, '오답 복습'));
    const visible = (x: ReviewTask) => opts.tabs !== true || kindFilter === 'all' || (kindFilter === 'evidence') === isEv(x);
    const cnt = (s: ReviewTask['state']) => tasks.filter((x) => x.state === s && visible(x)).length;
    const done = cnt('done');
    const solvableN = cnt('due') + cnt('overdue');
    if (opts.tabs === true) {
      // 복습 화면: 오늘 / 기한 지남 / 예정 / 완료 중 하나만 보인다(한 화면에 모두 펼치지 않는다). 지점 단위(GR) 복습이며 풀 수 있는 것부터 보인다.
      const tabs: { key: ReviewTask['state']; label: string }[] = [{ key: 'due', label: '오늘' }, { key: 'overdue', label: '기한 지남' }, { key: 'upcoming', label: '예정' }, { key: 'done', label: '완료' }];
      if (tab === null) tab = (['due', 'overdue', 'upcoming', 'done'] as const).find((k) => cnt(k) > 0) ?? 'due';
      const kinds: { key: ReviewKindFilter; label: string }[] = [{ key: 'all', label: '전체' }, { key: 'gr', label: 'GR 오답' }, { key: 'evidence', label: '근거 문제' }];
      box.append(h('div', { class: 'tabbar', role: 'group', 'aria-label': '복습 종류', 'data-testid': 'rt-kinds' },
        ...kinds.map((k) => toggleButton(k.label, kindFilter === k.key, `rt-kind-${k.key}`, () => { kindFilter = k.key; page = 1; tab = null; void load(); }))));
      box.append(h('div', { class: 'tabbar', role: 'group', 'aria-label': '복습 상태', 'data-testid': 'rt-tabs' },
        ...tabs.map((x) => toggleButton(`${x.label} ${cnt(x.key)}`, tab === x.key, `rt-tab-${x.key}`, () => { tab = x.key; page = 1; void load(); }))));
      box.append(h('p', { class: 'ev-note' }, 'GR 오답은 오답으로 채점된 지점 하나가, 근거 문제는 근거를 틀리거나 풀지 못한 지점 하나가 복습 하나예요. 문제 전체를 다시 푸는 것이 아니에요.'));
      const list = tasks.filter((x) => x.state === tab && visible(x));
      if (list.length === 0) box.append(h('p', { class: 'ev-note', 'data-testid': 'rt-tab-empty' }, '이 상태의 복습이 없어요.'));
      const shown = list.slice(0, page * PAGE);
      const group = TASK_GROUPS.find((g) => g.state === tab);
      box.append(h('div', { 'data-testid': `rt-group-${tab}` }, group !== undefined ? h('p', { class: 'ev-note' }, group.hint) : null, h('ul', { class: 'alist' }, ...shown.map((x) => taskRow(x)))));
      if (list.length > shown.length) box.append(h('button', { class: 'secondary', type: 'button', 'data-testid': 'rt-more', onclick: () => { page += 1; void load(); } }, `더 보기 (${list.length - shown.length}개 남음)`));
    } else {
      // 홈: 풀 때가 된 복습만 몇 개 보이고 나머지는 "전체 보기"로 복습 화면에서 본다
      const lim = opts.limit ?? 3;
      let left = lim;
      for (const g of TASK_GROUPS) {
        const list = tasks.filter((x) => x.state === g.state);
        if (list.length === 0) continue;
        const take = g.state === 'upcoming' ? [] : list.slice(0, Math.max(0, left));
        left -= take.length;
        box.append(h('div', { 'data-testid': `rt-group-${g.state}` }, h('h4', {}, `${g.title} (${list.length})`), g.state === 'upcoming' ? h('p', { class: 'ev-note' }, `${g.hint} 복습 화면에서 볼 수 있어요.`) : h('p', { class: 'ev-note' }, g.hint),
          take.length > 0 ? h('ul', { class: 'alist' }, ...take.map((x) => taskRow(x))) : null));
      }
      if (opts.onMore !== undefined && (solvableN > lim || cnt('upcoming') > 0 || done > 0)) box.append(h('button', { class: 'secondary', type: 'button', 'data-testid': 'rt-all', onclick: opts.onMore }, '복습 전체 보기'));
    }
    if (done > 0) box.append(h('p', { class: 'ev-note', 'data-testid': 'rt-done' }, `끝낸 복습 ${done}개`));
    // 개발용 정책 표시는 학생 본문에 크게 두지 않는다. 학생에게는 실제 예정일과 행동 안내만 보인다(제안 상태는 원장 정책 영역·인수 문서에 남는다).
    box.append(h('p', { class: 'ev-note', 'data-testid': 'rt-policy' }, '맞히면 다음 복습까지 간격이 길어지고, 틀리면 짧아져요.'));
    root.append(box);
    return true;
  }

  function taskRow(x: ReviewTask): HTMLElement {
    const canSolve = solvable(x);
    const noEvidence = (x.state === 'due' || x.state === 'overdue') && isEv(x) && x.evidenceUnavailable === true;
    const li = h('li', { 'data-testid': `rt-${x.itemId}`, 'data-state': x.state, 'data-kind': isEv(x) ? 'evidence' : 'gr' },
      h('span', { class: 'at' }, `${x.releaseTitle} · ${x.displayNumber}번 · ${isEv(x) ? '근거 문제' : 'GR 오답'}`),
      noEvidence ? h('span', { class: 'ev-note', 'data-testid': `rt-unavail-${x.itemId}` }, '근거 자료 없음 — 지금은 풀 수 있는 근거 문제가 없어요. 통과로 세지 않아요.') : null,
      h('span', { class: 'ameta' }, `${x.state === 'upcoming' ? '예정' : '복습일'} ${dayText(x.dueAt)} · ${x.rounds}번 복습함 · ${x.stage}/${x.totalStages}단계`),
      canSolve ? h('button', { class: 'primary', type: 'button', 'data-testid': `rt-open-${x.itemId}`, onclick: () => {
        open = open === x.itemId ? null : x.itemId;
        if (open !== null && session.total === 0) session.total = scopeCount();
        void drawPanel(li, x);
      } }, '복습하기') : null);
    rows.set(x.itemId, { li, x });
    return li;
  }

  async function drawPanel(li: HTMLElement, x: ReviewTask): Promise<void> {
    li.querySelector('.rt-panel')?.remove();
    if (open !== x.itemId) return;
    const panel = h('div', { class: 'rt-panel', 'data-testid': `rt-panel-${x.itemId}` }, h('p', { class: 'loading' }, '문제를 불러오고 있어요'));
    li.append(panel);
    if (isEv(x)) { drawEvidencePanel(panel, x); return; }
    const r = await t.request('GET', `/attempts/${x.attemptId}/items`);
    if (!live()) return;
    clear(panel);
    if (r.kind !== 'http' || r.status !== 200) { panel.append(h('p', { class: 'notice', role: 'alert' }, '문제를 불러오지 못했어요.'), h('button', { class: 'secondary', onclick: () => void drawPanel(li, x) }, '다시 불러오기')); return; }
    const items = r.body as unknown as ItemsResponse;
    const it = items.items.find((i) => i.responseMode === 'gr-points' && i.grPoints.some((g) => g.id === x.pointId));
    const view = it?.grPoints.find((g) => g.id === x.pointId);
    if (it === undefined || view === undefined) { panel.append(h('p', { class: 'notice', role: 'alert' }, '이 문제를 찾지 못했어요.')); return; }
    const multiple = items.pointModes?.[x.pointId] === 'multiple';
    const draft = new Set<string>();
    const msg = h('p', { role: 'status', 'data-testid': `rt-msg-${x.itemId}` });
    const box = h('span', { class: 'toggle-group', role: 'group', 'aria-label': `${x.displayNumber}번 보기` });
    const go = h('button', { class: 'primary', type: 'button', 'data-testid': `rt-submit-${x.itemId}` }, '이 지점 제출');
    const redraw = () => {
      clear(box);
      for (const o of view.options) {
        box.append(toggleButton(o.label, draft.has(o.id), `rt-opt-${o.id}`, () => { if (draft.has(o.id)) draft.delete(o.id); else { if (!multiple) draft.clear(); draft.add(o.id); } redraw(); }));
      }
    };
    redraw();
    // 원문 문맥은 그대로 보이되, 다른 지점의 빈칸은 "( ? )"로 둔다(내 이전 답이나 정답을 비추지 않는다)
    const para = h('p', { class: 'rgr' });
    for (const s of it.segments ?? []) {
      if (s.kind === 'text') { para.append(s.text); continue; }
      if (s.pointId === x.pointId) para.append(h('span', { class: 'pt-no' }, x.displayNumber), box);
      else {
        // 등록 원문에는 정답 표현이 따로 없고 "(n) [보기 / 보기]" 형태의 보기만 있다. 그 보기를 그대로 빨간 원문 참조로 보인다.
        // 정답을 고르거나 지어내지 않는다. 보기를 알 수 없으면 (?)로 두어 불가 상태를 밝힌다.
        const other = it.grPoints.find((g) => g.id === s.pointId);
        if (other === undefined || other.options.length === 0) para.append(' ( ? ) ');
        else para.append(' ', h('span', { class: 'ref-src', 'data-testid': 'rt-ref', title: '원문 참조(풀지 않는 지점)' }, `${other.displayNumber} [${other.options.map((o) => o.label).join(' / ')}]`), ' ');
      }
    }
    const progress = progressLine(`GR 오답 복습 · 지점 ${x.displayNumber}`);
    const legend = h('p', { class: 'ev-note rt-legend', 'data-testid': 'rt-legend' }, '빨간 글씨는 지문에 나온 다른 지점의 원문 보기예요(원문 참조). 틀렸다는 표시가 아니에요. 지금은 ', h('strong', {}, `${x.displayNumber}번`), '만 골라요.');
    go.onclick = () => submitAnswer(x, [...draft].sort(), 'selectedOptionIds', go, msg);
    panel.append(progress, legend, h('section', { class: 'item' }, h('div', { class: 'item-h' }, h('span', { class: 'no', 'data-testid': 'rt-itemno' }, String(it.number) === x.displayNumber ? `${x.displayNumber}번` : `문항 ${it.number}번 · 이 중 지점 ${x.displayNumber}번을 풀어요`)), para), msg, go);
  }

  const scope = () => ({ tab, kind: kindFilter, tabs: opts.tabs === true });
  /** 현재 탭·종류 범위에서 지금 풀 수 있는 항목 수(진행 표시용) */
  const scopeCount = (): number => pickNext(tasks, scope(), '').scopeLeft;

  function progressLine(label: string): HTMLElement {
    const n = scopeCount();
    return h('p', { class: 'rt-progress', 'data-testid': 'rt-progress' },
      h('span', { class: 'rt-pointno' }, label),
      h('span', {}, `오늘 복습 ${session.done + 1}/${Math.max(session.total, session.done + n)}`));
  }

  /** 근거 재확인: GR 선택 화면이 아니라 근거 객관식(보기 하나)이다. 정답 번호·해설은 서버가 보내지 않는다. 제출 본문은 selectedOptionId */
  function drawEvidencePanel(panel: HTMLElement, x: ReviewTask): void {
    clear(panel);
    const q = x.evidenceQuestion;
    if (q === null || q === undefined) { panel.append(h('p', { class: 'notice', role: 'alert', 'data-testid': `rt-unavail-${x.itemId}` }, '근거 자료 없음 — 풀 수 있는 근거 문제가 없어요. 통과로 세지 않아요.')); return; }
    let chosen: string | null = null;
    const msg = h('p', { role: 'status', 'data-testid': `rt-msg-${x.itemId}` });
    const box = h('div', { class: 'toggle-group', role: 'group', 'aria-label': `${x.displayNumber}번 근거 보기` });
    const go = h('button', { class: 'primary', type: 'button', 'data-testid': `rt-submit-${x.itemId}` }, '근거 제출');
    const redraw = () => {
      clear(box);
      for (const o of q.options) box.append(toggleButton(`${o.no}. ${o.text}`, chosen === o.id, `rt-evopt-${o.id}`, () => { chosen = o.id; redraw(); }));
    };
    redraw();
    go.onclick = () => submitAnswer(x, chosen === null ? [] : [chosen], 'selectedOptionId', go, msg);
    panel.append(progressLine(`근거 문제 복습 · 지점 ${x.displayNumber}`),
      h('p', { class: 'ev-note' }, '이 지점이 왜 그렇게 쓰였는지 근거를 하나 골라요. 정답은 제출한 뒤에도 여기서 알려 주지 않아요.'),
      h('section', { class: 'item' }, h('p', { class: 'ev-stem', 'data-testid': `rt-evstem-${x.itemId}` }, q.stem), box), msg, go);
  }

  async function submitAnswer(x: ReviewTask, ids: string[], field: 'selectedOptionIds' | 'selectedOptionId', go: HTMLButtonElement, msg: HTMLElement): Promise<void> {
    if (ids.length === 0) { msg.textContent = `${x.displayNumber}번: 보기를 골라 주세요.`; return; }
    const sig = ids.join(',');
    const prev = keys.get(x.itemId);
    const key = prev !== undefined && prev.sig === sig ? prev.key : newRequestKey();
    keys.set(x.itemId, { sig, key });
    go.disabled = true;
    const a = await t.request('POST', `/me/review-tasks/${x.itemId}/answer`, { key, body: { [field]: field === 'selectedOptionId' ? ids[0] : ids } });
    if (!live()) return;
    if (a.kind !== 'http') { go.disabled = false; msg.textContent = '제출됐는지 확인하지 못했어요. 같은 답으로 다시 눌러 주세요.'; return; }
    keys.delete(x.itemId); open = null;
    if (a.status === 200) {
      const ok = a.body['result'] === 'correct';
      const next = a.body['completed'] === true ? '이 복습은 모두 끝났어요.' : `다음 복습: ${dayText(a.body['nextDueAt'] as string | null)}`;
      session.done += 1;
      const what = field === 'selectedOptionId' ? '근거' : '';
      const note = `${x.displayNumber}번 ${what}: ${ok ? '맞았어요.' : '틀렸어요. 해설은 결과 화면의 \'정답·해설 보기\'에서 볼 수 있어요.'} ${next}`;
      // 응답은 이미 서버에 저장됐다. 목록 갱신이 실패하면 "완료"로 읽지 않고 오류·재시도만 보인다(세션 진행은 유지).
      if (!(await load(note))) return;
      // 다음 항목은 현재 탭·종류 범위의 서버 목록에서 고른다. 화면에 그려져 있는지는 완료 판단과 무관하다.
      let pick = pickNext(tasks, scope(), x.itemId);
      let row = pick.next === null ? undefined : rows.get(pick.next.itemId);
      if (pick.next !== null && row === undefined && opts.tabs === true) {
        // 다음 페이지에 있다: 그 항목이 보이는 페이지까지 늘려 다시 그린 뒤 연다
        const target = pick.next.itemId;
        const shownList = tasks.filter((y) => y.state === tab && (kindFilter === 'all' || (kindFilter === 'evidence') === isEv(y))); // 화면 목록과 같은 기준(풀 수 없는 근거 항목 포함)
        page = Math.max(page, Math.floor(Math.max(0, shownList.findIndex((y) => y.itemId === target)) / PAGE) + 1);
        if (!(await load(note))) return;
        pick = pickNext(tasks, scope(), x.itemId);
        row = pick.next === null ? undefined : rows.get(pick.next.itemId);
      }
      if (row !== undefined) {
        open = row.x.itemId; await drawPanel(row.li, row.x);
        // 방금 제출한 결과를 다음 항목 패널 맨 위에도 보인다(상단 알림은 화면 밖일 수 있음). 같은 내용의 복사본이며 읽기 전용 안내다.
        const panelEl = row.li.querySelector('.rt-panel');
        const near = h('p', { class: 'notice-ok', role: 'status', 'data-testid': 'rt-last-result' }, `방금 제출: ${note}`);
        if (panelEl !== null) panelEl.prepend(near); else row.li.append(near);
        near.scrollIntoView?.({ block: 'nearest' });
        return;
      }
      const after = root.querySelector('[data-testid="rt-note"]');
      if (pick.next !== null) {
        // 풀 항목이 남았는데 이 화면에 없다(홈의 제한 목록): 완료가 아니라 이동 안내
        after?.after(h('p', { class: 'notice', role: 'status', 'data-testid': 'rt-remaining' }, `아직 풀 복습이 ${pick.scopeLeft}개 남았어요. 복습 화면에서 이어서 풀 수 있어요.`));
      } else if (session.total > 1 || session.done > 1) {
        const more = pick.otherSolvable > 0 ? ` 다른 탭·종류에 풀 복습이 ${pick.otherSolvable}개 남아 있어요.` : ' 지금 풀 복습이 모두 끝났어요.';
        after?.after(h('p', { class: 'ev-note', 'data-testid': pick.otherSolvable > 0 ? 'rt-summary-scope' : 'rt-summary' }, `${pick.otherSolvable > 0 ? '이 범위' : '오늘'} 복습 ${session.done}개를 풀었어요.${more}`));
      } else if (pick.otherSolvable > 0) {
        after?.after(h('p', { class: 'ev-note', 'data-testid': 'rt-summary-scope' }, `이 범위의 복습은 끝났어요. 다른 탭·종류에 풀 복습이 ${pick.otherSolvable}개 남아 있어요.`));
      }
      if (pick.next === null) { session.total = 0; session.done = 0; }
    } else {
      await load(a.body['error'] === 'not_due' ? `${x.displayNumber}번은 아직 복습할 때가 아니에요.` : a.body['error'] === 'evidence_unavailable' ? `${x.displayNumber}번: 근거 자료 없음 — 풀 수 있는 근거 문제가 없어요.` : `${x.displayNumber}번: 제출하지 못했어요. 상태를 다시 불러왔어요.`);
    }
  }

  void load();
}
